import { useEffect, useState } from 'react';
import { X, Export, DotsThreeVertical } from '@phosphor-icons/react';
import TanowMark from '@/components/TanowMark';

const KEY = 'tendwell_a2hs_dismissed';
let deferredPrompt = null;

if (typeof window !== 'undefined') {
    window.addEventListener('beforeinstallprompt', (e) => {
        e.preventDefault();
        deferredPrompt = e;
        window.dispatchEvent(new Event('tendwell:installable'));
    });
    window.addEventListener('appinstalled', () => {
        deferredPrompt = null;
        try { localStorage.setItem(KEY, 'installed'); } catch { /* ignore */ }
        window.dispatchEvent(new Event('tendwell:installed'));
    });
}

function detectPlatform() {
    const ua = navigator.userAgent || '';
    const isIOS = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
    const isAndroid = /Android/.test(ua);
    const isSafariIOS = isIOS && !/CriOS|FxiOS|EdgiOS|OPiOS|OPT\//.test(ua);
    const standalone = window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator.standalone === true;
    return { isIOS, isAndroid, isSafariIOS, standalone };
}

function useDynamicManifest(startUrl) {
    useEffect(() => {
        if (!startUrl) return;
        const link = document.querySelector('link[rel="manifest"]');
        if (!link) return;
        const original = link.getAttribute('href');
        const origin = window.location.origin;
        const manifest = {
            name: 'Tanow board',
            short_name: 'Tanow',
            start_url: origin + startUrl,
            scope: origin + '/',
            display: 'standalone',
            background_color: '#FBF7EF',
            theme_color: '#0C3B35',
            icons: [
                { src: `${origin}/icons/icon-192.png`, sizes: '192x192', type: 'image/png' },
                { src: `${origin}/icons/icon-512.png`, sizes: '512x512', type: 'image/png' },
                { src: `${origin}/icons/icon-512-maskable.png`, sizes: '512x512', type: 'image/png', purpose: 'maskable' },
            ],
        };
        link.setAttribute('href', 'data:application/manifest+json,' + encodeURIComponent(JSON.stringify(manifest)));
        return () => { if (original) link.setAttribute('href', original); };
    }, [startUrl]);
}

export default function AddToHomeScreenTip({ startUrl = null, tone = 'light' }) {
    useDynamicManifest(startUrl);
    const [visible, setVisible] = useState(false);
    const [platform, setPlatform] = useState(null);
    const [canPrompt, setCanPrompt] = useState(!!deferredPrompt);

    useEffect(() => {
        let dismissed = false;
        try { dismissed = !!localStorage.getItem(KEY); } catch { /* ignore */ }
        const p = detectPlatform();
        if (p.standalone) {
            try { localStorage.setItem(KEY, 'installed'); } catch { /* ignore */ }
            return;
        }
        if (dismissed || !(p.isSafariIOS || p.isAndroid)) return;
        setPlatform(p);
        setVisible(true);
        const onInstallable = () => setCanPrompt(true);
        const onInstalled = () => setVisible(false);
        window.addEventListener('tendwell:installable', onInstallable);
        window.addEventListener('tendwell:installed', onInstalled);
        return () => {
            window.removeEventListener('tendwell:installable', onInstallable);
            window.removeEventListener('tendwell:installed', onInstalled);
        };
    }, []);

    const dismiss = () => {
        try { localStorage.setItem(KEY, 'dismissed'); } catch { /* ignore */ }
        setVisible(false);
    };

    const install = async () => {
        if (!deferredPrompt) return;
        const evt = deferredPrompt;
        deferredPrompt = null;
        evt.prompt();
        const choice = await evt.userChoice.catch(() => null);
        if (choice?.outcome === 'accepted') {
            try { localStorage.setItem(KEY, 'installed'); } catch { /* ignore */ }
            setVisible(false);
        } else {
            dismiss();
        }
    };

    if (!visible || !platform) return null;

    const dark = tone === 'dark';
    const shell = dark
        ? 'bg-[#0C3B35] text-white border-[#0a2f2a]'
        : 'bg-white text-slate-900 border-slate-200';
    const sub = dark ? 'text-white/75' : 'text-slate-600';

    return (
        <div
            data-testid="a2hs-tip"
            role="status"
            className={`fixed inset-x-3 z-50 rounded-2xl border shadow-lg ${shell}`}
            style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}
        >
            <div className="flex items-start gap-3 p-3 pr-2">
                <div className={`shrink-0 h-11 w-11 rounded-xl flex items-center justify-center ${dark ? 'bg-white/10' : 'bg-[#0C3B35]'}`}>
                    <TanowMark size={26} tone="dark" />
                </div>
                <div className="flex-1 min-w-0 pt-0.5">
                    <div className="text-sm font-semibold leading-tight" data-testid="a2hs-title">Keep Tanow one tap away</div>
                    {platform.isSafariIOS ? (
                        <div className={`text-[13px] mt-1 leading-snug ${sub}`} data-testid="a2hs-text">
                            Tap <Export size={14} weight="bold" className="inline -mt-0.5 mx-0.5" aria-label="Share" /> <b>Share</b>, then <b>Add to Home Screen</b>.
                        </div>
                    ) : canPrompt ? (
                        <div className={`text-[13px] mt-1 leading-snug ${sub}`} data-testid="a2hs-text">
                            Add it to your home screen so it opens like an app.
                        </div>
                    ) : (
                        <div className={`text-[13px] mt-1 leading-snug ${sub}`} data-testid="a2hs-text">
                            Tap <DotsThreeVertical size={14} weight="bold" className="inline -mt-0.5 mx-0.5" aria-label="Menu" /> <b>menu</b>, then <b>Add to Home screen</b>.
                        </div>
                    )}
                    {platform.isAndroid && canPrompt && (
                        <button
                            type="button"
                            data-testid="a2hs-install-button"
                            onClick={install}
                            className={`mt-2 inline-flex items-center justify-center rounded-full px-4 min-h-[44px] text-sm font-semibold ${dark ? 'bg-white text-[#0C3B35]' : 'bg-[#0C3B35] text-white'}`}
                        >
                            Add to home screen
                        </button>
                    )}
                </div>
                <button
                    type="button"
                    data-testid="a2hs-dismiss"
                    onClick={dismiss}
                    aria-label="Dismiss"
                    className={`shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full ${dark ? 'hover:bg-white/10 text-white/80' : 'hover:bg-slate-100 text-slate-500'}`}
                >
                    <X size={18} weight="bold" />
                </button>
            </div>
        </div>
    );
}
